import React, { ReactElement, useEffect, useState } from 'react'
import {
  StyleSheet,
  PressableProps,
  View,
  ViewStyle,
  TextStyle,
  Pressable,
  StyleProp,
  NativeSyntheticEvent,
  TargetedEvent,
  Platform,
} from 'react-native'

import { Color, Radius, Spacing } from '../../style'
import { Text } from '../typography'

import Checkbox from './checkbox'

interface StyleConfig {
  container?: ViewStyle
  text?: TextStyle
  bullet?: ViewStyle
}

type Config = {
  default?: StyleConfig
  disabled?: StyleConfig
  pressed?: StyleConfig
  hovered?: StyleConfig
}

const colorConfig: Config = {
  default: StyleSheet.create({
    container: {
      backgroundColor: Color.simplyWhite,
    },
    text: {
      color: Color.premiumBlack,
    },
  }),
  disabled: StyleSheet.create({
    container: {
      backgroundColor: Color.sleepingGrey,
      borderColor: Color.calmGrey,
    },
    text: {
      color: Color.calmGrey,
    },
    bullet: {
      backgroundColor: Color.sleepingGrey,
      borderColor: Color.calmGrey,
    },
  }),
  pressed: StyleSheet.create({
    container: {
      borderColor: Color.lightGreyBlue,
      backgroundColor: Color.selectedBlue,
    },
    text: {
      color: Color.premiumBlack,
    },
    bullet: {
      borderColor: Color.greyBlue,
    },
  }),
  /*eslint-disable react-native/no-unused-styles */
  hovered: StyleSheet.create({
    container: {
      borderWidth: 2,
      margin: -1,
      minHeight: 42,
      minWidth: 42,
    },
    text: {},
    bullet: {
      borderColor: Color.premiumBlack,
    },
  }),
  /*eslint-enable react-native/no-unused-styles */
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Color.calmGrey,
    minHeight: 40,
    minWidth: 40,
  },
  active: {
    borderColor: Color.greyBlue,
    borderWidth: 2,
    margin: -2,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 11,
  },
  bullet: {},
  text: {
    lineHeight: 16,
  },
  outlineError: {
    borderColor: Color.soothingPink,
  },
  selectedError: {
    backgroundColor: Color.lightPink,
  },
})

export interface Props extends Omit<PressableProps, 'style'> {
  title?: string | ReactElement
  style?: StyleProp<ViewStyle>
  contentContainerStyle?: StyleProp<ViewStyle>
  error?: boolean
  selected?: boolean
  round?: boolean
}

const CheckboxSquare = (props: Props) => {
  const { title, contentContainerStyle, error, selected, round, disabled, onFocus, onBlur, style, ...otherProps } =
    props
  const [active, setActive] = useState(false)

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }
  useEffect(() => {
    if (disabled) setActive(false)
  }, [disabled])

  const renderContent = (pressed: boolean, hovered?: boolean) => {
    const textStyle = [
      styles.text,
      colorConfig?.default?.text,
      hovered && colorConfig?.hovered?.text,
      (pressed || selected) && colorConfig?.pressed?.text,
      disabled && colorConfig?.disabled?.text,
    ]

    const checkboxStyle = [
      styles.bullet,
      !!title && { marginRight: Spacing.XS },
      hovered && colorConfig?.hovered?.bullet,
      (pressed || selected) && colorConfig?.pressed?.bullet,
      disabled && colorConfig?.disabled?.bullet,
      error && styles.outlineError,
    ]

    return (
      <View style={styles.content}>
        <Checkbox boxStyle={checkboxStyle} value={selected} disabled />
        {!!title && typeof title === 'string' ? <Text style={textStyle}>{title}</Text> : title}
      </View>
    )
  }

  const renderCheckboxSquare = (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state

    const borderRadius = (round ? Radius.L : Radius.S) + (!active && hovered ? 1 : 0)

    const containerStyle = [
      styles.container,
      { borderRadius },
      colorConfig?.default?.container,
      !active && hovered && colorConfig?.hovered?.container,
      (pressed || selected) && colorConfig?.pressed?.container,
      contentContainerStyle,
      !title && { paddingHorizontal: 0 },
      disabled && colorConfig.disabled?.container,
      error && styles.outlineError,
      error && (pressed || selected) && styles.selectedError,
    ]

    const outlineStyle = active && [styles.active, { borderRadius: borderRadius + 2 }, error && styles.outlineError]

    return (
      <View style={outlineStyle}>
        <View style={containerStyle}>{renderContent(pressed, hovered)}</View>
      </View>
    )
  }

  return (
    <Pressable
      disabled={disabled}
      onFocus={updateActive(true)}
      onBlur={updateActive(false)}
      style={[
        Platform.select({
          // @ts-expect-error outline style none is not defined
          web: {
            outlineStyle: 'none',
          } as ViewStyle,
        }),
        style,
      ]}
      testID="CheckboxSquare.Container"
      {...otherProps}
    >
      {renderCheckboxSquare}
    </Pressable>
  )
}

export default CheckboxSquare
